'use client';

import { useState } from 'react';

import { Textfit } from '@wwe-portal/ui/components/text-fit';

import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';
import { cn } from '@wwe-portal/ui/lib/lib';

import { TimeLeftCountdown } from '../time-left-countdown';

import { InfoIcon } from './info-icon';
import { useTimeLeft } from '../../../lib/hooks';

export interface StepUpBannerDesktopProps {
  endDate: string;
  banner: string;
  description?: string;
  wrapperProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >;
}

export type StepUpBannerComponent = React.Component<StepUpBannerDesktopProps>;

function Container({
  wrapperProps,
  children,
}: Pick<StepUpBannerDesktopProps, 'wrapperProps'> & {
  children: React.ReactNode;
}) {
  const { className, ...props } = wrapperProps || {};
  return (
    <div
      className={cn('relative h-full w-full cursor-pointer', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function ShopStepUpBanner1800({
  endDate,
  banner,
  description,
  wrapperProps,
}: Readonly<StepUpBannerDesktopProps>): React.JSX.Element {
  return (
    <Container wrapperProps={wrapperProps}>
      <div className="absolute inset-0 overflow-hidden bg-[#1F233F]">
        <div className="absolute bottom-[calc(14000%/379)] left-0 right-0 top-0 rounded-[10px]">
          <div className="relative h-full w-full bg-[#1F233F]">
            <div className="absolute inset-x-0 bottom-0 top-[calc(5700%/379)]">
              <img
                alt="step up banner"
                className="h-full w-full object-fill"
                src={banner}
              />
              <div className="absolute right-[12px] top-[12px] text-white">
                <InfoIcon size={23} />
              </div>
              <div className="absolute inset-x-0 -top-[4px] h-[calc(4100%/379)] rotate-180 bg-[linear-gradient(180deg,rgba(31,35,63,0)_-0.47%,#1F233F_65%)]" />
              <div className="absolute inset-x-0 bottom-0 h-[calc(5200%/180)] bg-[linear-gradient(180deg,rgba(31,35,63,0)_-0.47%,#1F233F_60%)]" />
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 flex h-[calc(5400%/180)] items-center justify-center bg-[#1F233F]">
          <div
            className={cn(
              'font-akagi max-w-[390px] text-center text-xl uppercase text-white'
            )}
          >
            {description}
          </div>
        </div>
        <div className="font-akagi absolute bottom-[calc(10200%/378)] flex w-full flex-col items-center justify-center gap-1 text-xl text-white">
          <TimeLeftCountdown endDate={endDate} iconSize={32} />
        </div>
      </div>
    </Container>
  );
}

export function ShopStepUpBanner1440({
  endDate,
  banner,
  description,
  wrapperProps,
}: Readonly<StepUpBannerDesktopProps>): React.JSX.Element {
  return (
    <Container wrapperProps={wrapperProps}>
      <div
        aria-hidden="true"
        className="absolute inset-0 cursor-pointer overflow-hidden bg-[#1F233F]"
      >
        <div className="absolute inset-0">
          <img
            alt="step up banner"
            className="h-full w-full object-fill"
            src={banner}
          />
        </div>
        <div className="absolute right-[12px] top-[12px] text-white">
          <InfoIcon size={28} />
        </div>
        <div className="absolute inset-x-0 bottom-0 h-[calc(18100%/379)] bg-[linear-gradient(180deg,rgba(31,35,63,0)_-0.47%,#1F233F_44.16%)]" />
        <div className="absolute bottom-0 left-0 right-0 flex h-[calc(8500%/324)] items-center justify-center">
          <div
            className={cn(
              'font-akagi max-w-[535px] text-center text-[22px] uppercase text-white'
            )}
          >
            {description}
          </div>
        </div>
        <div className="font-akagi absolute bottom-[calc(8400%/324)] flex w-full flex-row items-center justify-center gap-4 text-white">
          <div className="text-6">
            <TimeLeftCountdown endDate={endDate} iconSize={45} />
          </div>
        </div>
      </div>
    </Container>
  );
}

export function ShopStepUpBanner1280({
  endDate,
  banner,
  description,
  wrapperProps,
}: Readonly<StepUpBannerDesktopProps>): React.JSX.Element {
  return (
    <Container wrapperProps={wrapperProps}>
      <div
        aria-hidden="true"
        className="absolute inset-0 cursor-pointer overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="relative aspect-[829/328] w-full bg-[#1F233F]">
            <div className="absolute inset-0">
              <img
                alt="stepup banner"
                className="h-full w-full object-fill"
                src={banner}
              />
            </div>
          </div>
        </div>
        <div className="absolute right-[12px] top-[12px] text-white">
          <InfoIcon />
        </div>
        <div className="absolute inset-x-0 bottom-0 h-[calc(18100%/379)] bg-[linear-gradient(180deg,rgba(31,35,63,0)_-0.47%,#1F233F_44.16%)]" />
        <div className="absolute bottom-0 left-0 right-0 flex h-[calc(5400%/180)] items-center justify-center">
          <div
            className={cn(
              'font-akagi max-w-[535px] text-center text-[22px] uppercase text-white'
            )}
          >
            {description}
          </div>
        </div>
        <div className="font-akagi absolute bottom-[calc(10200%/378)] flex w-full flex-row items-center justify-center gap-4 text-white">
          <div className="text-6">
            <TimeLeftCountdown endDate={endDate} iconSize={45} />
          </div>
        </div>
      </div>
    </Container>
  );
}

export function DynamicCountdown({
  endDate,
  className,
}: Readonly<{
  endDate: string;
  className?: string;
}>) {
  const [fontSize, setFontSize] = useState<number>();
  const { ended, getTimeText } = useTimeLeft(endDate);

  if (ended) return null;
  return (
    <div className={cn('flex h-full items-center stroke-2', className)}>
      <Textfit
        className="w-[calc(4000%/118)]"
        mode="single"
        onReady={setFontSize}
      >
        <span>ENDS IN: </span>
      </Textfit>
      <div className="-mt-[5px] aspect-square w-1/5 self-stretch px-[5px]">
        <IcoMoonIcon
          iconName="timer"
          style={{
            width: '100%',

            height: '100%',
          }}
        />
      </div>
      <div
        className="w-[calc(5800%/118)] whitespace-nowrap break-keep"
        style={{ fontSize }}
      >
        {getTimeText()}
      </div>
    </div>
  );
}

export function ShopStepUpBanner390({
  endDate,
  banner,
  description,
  wrapperProps,
}: Readonly<StepUpBannerDesktopProps>): React.JSX.Element {
  return (
    <Container wrapperProps={wrapperProps}>
      <Banner390 banner={banner} description={description} endDate={endDate} />
    </Container>
  );
}

export function Banner390({
  endDate,
  banner,
  description,
}: Readonly<StepUpBannerDesktopProps>): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 cursor-pointer overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="relative aspect-[384/174] w-full bg-[#1F233F]">
          <div className="absolute inset-0">
            <img
              alt="stepup-banner"
              className="h-full w-full object-cover"
              src={banner}
            />
          </div>
        </div>
      </div>
      <div className="absolute right-[12px] top-[12px] text-white">
        <InfoIcon size={22} />
      </div>
      <div className="absolute inset-x-0 bottom-0 h-[calc(18100%/379)] bg-[linear-gradient(180deg,rgba(31,35,63,0)_-0.47%,#1F233F_44.16%)]" />
      <div className="absolute bottom-0 left-0 right-0 flex h-[calc(5400%/180)] items-center justify-center">
        <Textfit
          className={cn(
            'font-akagi max-h-full max-w-[calc(26000%/379)] text-center uppercase text-white'
          )}
        >
          {description}
        </Textfit>
      </div>
      <div className="font-akagi absolute bottom-[calc(10200%/378)] flex h-[calc(2200%/180)] w-full flex-row items-stretch justify-center gap-4 text-white">
        <DynamicCountdown
          className="aspect-[118/22] w-[calc(25000%/829)] uppercase"
          endDate={endDate}
        />
      </div>
    </div>
  );
}
